import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store';

export const selectOriginalServiceSlots = createSelector(
  (state: RootState) => state.timeSlots.availableTimeSlots,
  (state: RootState) => state.app.selected.storeId,
  (availableTimeSlots, storeId) => availableTimeSlots[storeId] || [],
);

export const selectReplacementServiceSlots = createSelector(
  (state: RootState) => state.timeSlots.replacementServiceAvailableTimeSlots,
  (state: RootState) => state.app.selected.replacementService?.storeId,
  (replacementServiceAvailableTimeSlots, storeId) =>
    replacementServiceAvailableTimeSlots[storeId] || [],
);

export const selectSelectedTimeSlot = createSelector(
  [
    (state: RootState) => state.app.selected.timeSlot,
    (state: RootState) => state.app.selected.replacementService?.timeSlot,
  ],
  (timeSlot, replacementTimeSlot) => timeSlot || replacementTimeSlot,
);

export const selectSelectedOriginalService = createSelector(
  [
    (state: RootState) => state.app.selected.serviceId,
    (state: RootState) => state.services.availableServices,
  ],
  (selectedServiceId, services) =>
    services.find((service) => service.serviceId === selectedServiceId),
);

export const selectServiceForTheSelectedTimeSlot = createSelector(
  [
    (state: RootState) => state.app.selected.replacementService?.timeSlot,
    selectSelectedOriginalService,
  ],
  (selectedReplacementTimeSlot, selectedOriginalService) => {
    if (selectedOriginalService) {
      return selectedReplacementTimeSlot
        ? selectedOriginalService.replacementService
        : selectedOriginalService;
    }
  },
);

export const selectServiceByServiceId = createSelector(
  [
    (state: RootState) => state.services.availableServices,
    (state: RootState, serviceId: string) => serviceId,
  ],
  (availableServices, serviceId) =>
    availableServices.find((service) => service.serviceId === serviceId),
);

export const selectServiceIdForTheSelectedTimeSlot = createSelector(
  [
    (state: RootState) => state.app.selected.replacementService?.timeSlot,
    (state: RootState) => state.app.selected.serviceId,
    selectSelectedOriginalService,
  ],
  (selectedReplacementTimeSlot, selectedOriginalServiceId, originalService) => {
    if (selectedReplacementTimeSlot) {
      return originalService?.replacementService?.serviceId || '';
    }
    return selectedOriginalServiceId;
  },
);

export const selectServiceCodeForTheSelectedTimeSlot = createSelector(
  [
    (state: RootState) => state.app.selected.replacementService?.timeSlot,
    (state: RootState) => state.app.selected.serviceCode,
    selectSelectedOriginalService,
  ],
  (
    selectedReplacementTimeSlot,
    selectedOriginalServiceCode,
    originalService,
  ) => {
    if (selectedReplacementTimeSlot) {
      return originalService?.replacementService?.serviceCode || '';
    }
    return selectedOriginalServiceCode;
  },
);

export const selectStoreIdForTheSelectedTimeSlot = createSelector(
  [
    (state: RootState) => state.app.selected.replacementService?.timeSlot,
    (state: RootState) => state.app.selected.storeId,
    (state: RootState) => state.app.selected.replacementService?.storeId,
  ],
  (replacementTimeSlot, originalStoreId, replacementStoreId) => {
    if (replacementTimeSlot) {
      return replacementStoreId;
    }
    return originalStoreId;
  },
);

export const selectStoreForTheSelectedTimeSlot = createSelector(
  [
    (state: RootState) => state.app.selected.replacementService?.timeSlot,
    (state: RootState) => state.app.selected.storeId,
    (state: RootState) => state.stores.availableStores,
    (state: RootState) => state.app.selected.replacementService?.store,
  ],
  (replacementTimeSlot, originalStoreId, originalStores, replacementStore) => {
    if (replacementTimeSlot) {
      return replacementStore || undefined;
    }
    return originalStores.find((store) => store.value === originalStoreId);
  },
);

export const selectAppOperation = (state: RootState) => state.app.operation;

export const selectOperationForTheSelectedTimeSlot = createSelector(
  [
    (state: RootState) => state.app.selected.replacementService?.timeSlot,
    selectAppOperation,
    selectSelectedOriginalService,
  ],
  (replacementTimeSlot, appOperation, selectedService) => {
    return replacementTimeSlot
      ? selectedService?.replacementService?.operation || appOperation
      : appOperation;
  },
);

export const selectLocationForTheSelectedTimeSlot = createSelector(
  [
    (state: RootState) => state.app.selected.replacementService?.timeSlot,
    (state: RootState) => state.app.selected.serviceId,
    (state: RootState) => state.services.availableServices,
  ],
  (replacementTimeSlot, selectedServiceId, services) => {
    const service = services.find(
      ({ serviceId }) => serviceId === selectedServiceId,
    );
    return replacementTimeSlot
      ? service?.replacementService?.location
      : service?.location;
  },
);
